import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Tooltip } from '@/components/Tooltip';
import { VerticalNavProps } from '@/sections/asset/types/navigation.types';
import { cn } from '@/utils/classname';

export function VerticalNavigationWithSubTabs({
  mainTabs,
  openMainTabs,
  selectedSubTab,
  onMainTabSelect,
  onSubTabSelect,
}: VerticalNavProps) {
  return (
    <div className="flex flex-col" data-testid="vertical-nav">
      {mainTabs.map(mainTab => {
        const isMainTabOpen = openMainTabs.has(mainTab.value);
        const currentSubTabs = mainTab.tabs || [];
        const isDisabled = mainTab.disabled !== undefined;

        // Check if this is a direct tab (single subtab with same value as parent)
        const isDirectTab =
          currentSubTabs.length === 1 &&
          currentSubTabs[0].value === mainTab.value;

        // For direct tabs, check if it's selected
        const isDirectTabSelected =
          isDirectTab && selectedSubTab?.value === mainTab.value;

        // If it's a direct tab, render it as a simple clickable tab
        if (isDirectTab) {
          const directTab = currentSubTabs[0];
          return (
            <div key={mainTab.value} className="flex flex-col">
              <Tooltip title={mainTab.disabled} placement="left">
                <button
                  onClick={() => !isDisabled && onSubTabSelect?.(directTab)}
                  disabled={isDisabled}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors',
                    'border-chariot-stroke rounded-none border-y',
                    isDirectTabSelected
                      ? 'bg-chariot-background-secondary text-chariot-text-primary font-medium shadow-xs'
                      : isDisabled
                        ? 'text-chariot-text-disabled cursor-not-allowed opacity-50'
                        : 'text-chariot-text-secondary hover:bg-chariot-background-secondary hover:text-chariot-text-primary'
                  )}
                  role="tab"
                  aria-selected={isDirectTabSelected}
                  aria-disabled={isDisabled}
                >
                  {mainTab.icon && (
                    <mainTab.icon
                      className={cn(
                        'size-4 shrink-0 transition-colors',
                        isDirectTabSelected
                          ? 'text-chariot-text-primary'
                          : 'text-chariot-text-secondary'
                      )}
                    />
                  )}
                  <span>{mainTab.label}</span>
                </button>
              </Tooltip>
            </div>
          );
        }

        // Original dropdown behavior for tabs with multiple subtabs
        return (
          <div key={mainTab.value} className="flex flex-col">
            <Tooltip title={mainTab.disabled} placement="left">
              <button
                onClick={() => !isDisabled && onMainTabSelect(mainTab)}
                disabled={isDisabled}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors',
                  'border-chariot-stroke rounded-none border-y',
                  isDisabled
                    ? 'text-chariot-text-disabled cursor-not-allowed opacity-50'
                    : 'text-chariot-text-secondary hover:bg-chariot-background-secondary hover:text-chariot-text-primary'
                )}
                role="tab"
                aria-selected={isMainTabOpen}
                aria-disabled={isDisabled}
              >
                {isMainTabOpen ? (
                  <ChevronDownIcon className="text-chariot-text-secondary size-4 shrink-0" />
                ) : (
                  <ChevronRightIcon className="text-chariot-text-secondary size-4 shrink-0" />
                )}
                <span>{mainTab.label}</span>
              </button>
            </Tooltip>

            {!isDisabled && isMainTabOpen && currentSubTabs.length > 0 && (
              <div className="flex flex-col space-y-1">
                {currentSubTabs.map(subTab => {
                  const isSubTabSelected =
                    selectedSubTab?.value === subTab.value;
                  const isSubTabDisabled = subTab.disabled !== undefined;

                  return (
                    <Tooltip
                      key={subTab.value}
                      title={subTab.disabled}
                      placement="left"
                    >
                      <button
                        onClick={() => onSubTabSelect?.(subTab)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-none border border-transparent px-3 py-2 pl-8 text-left text-xs transition-colors',
                          'hover:bg-chariot-background-tertiary',
                          isSubTabSelected
                            ? 'bg-chariot-background-secondary text-chariot-text-primary font-medium shadow-xs'
                            : 'text-chariot-text-tertiary hover:text-chariot-text-secondary',
                          isSubTabDisabled
                            ? 'text-chariot-text-disabled cursor-not-allowed opacity-50'
                            : ''
                        )}
                        aria-disabled={isSubTabDisabled}
                        disabled={isSubTabDisabled}
                        role="button"
                      >
                        {subTab.icon && (
                          <subTab.icon
                            className={cn(
                              'size-3 shrink-0 transition-colors',
                              isSubTabSelected
                                ? 'text-chariot-text-primary'
                                : 'text-chariot-text-tertiary'
                            )}
                          />
                        )}
                        <span>{subTab.label}</span>
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
